import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import ErrorModal from "../../../reusableComponents/ErrorModal";
import SuccessModal from "../../../reusableComponents/SuccessModal";
import {removeLastPayment} from "../../../store/reducers/accountSlice";
import {getPaymentStatus} from "../../../utils/apis";
import "./index.scss";
import {getPaymentStatusPublic} from "../../../utils/apis/payment";
import ProcessingModal from "../../../reusableComponents/processing modal";

import Confetti from "react-confetti";
import successSound from "../../../assets/SoundFiles/tada-fanfare-a-6313.mp3";
import {useWindowSize} from "react-use";

// The processing screen is driven by the payment status API instead of a fixed wait:
// we re-check every POLL_INTERVAL_MS and close it the moment the webhook has landed,
// so it stays on screen only for as long as the confirmation actually takes.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 45000;

// Every payment-result route renders this component. The provider redirect only
// says where the user came back from - the webhook is what actually settles a
// payment - so the status API is queried on these routes too, and a charge that
// did go through is honoured even when the redirect claims otherwise.
const ABANDONED_STATUSES = ["cancelled", "declined", "failure"];

// The status API answers 200 for any payment it can find - including a failed one
// ({..., "payment_status": "failed"}) - so the HTTP code only says the lookup
// worked. payment_status is what actually says how the payment went, and these are
// the values the backend uses (same vocabulary as the payment history filter).
const SUCCEEDED_PAYMENT_STATUSES = ["succeeded", "success", "successful", "completed", "captured", "paid"];
const PENDING_PAYMENT_STATUSES = ["open", "pending", "processing", "created", "initiated", "in_progress"];

// Everything the API can report that is neither settled nor still moving.
const FAILURE_MESSAGES = {
  failed: "You have not been charged. Please try again or use a different payment method.",
  declined: "Your card issuer declined the payment. Please try again with a different card.",
  rejected: "The payment was rejected, so no account was created. Please try again or contact support.",
  expired: "The payment session expired before it completed. Please start the checkout again.",
  cancelled: "Nothing has been charged. You can start the checkout again whenever you are ready.",
};
const PENDING_MESSAGE = "Payment is in progress, if successful you will receive your account in 15-30 mins";
const UNKNOWN_MESSAGE = "Please check your payment history in a few minutes - if the amount was charged, your account will follow shortly.";

// Every outcome names itself in the popup heading instead of a blanket "Error".
const STATUS_TITLES = {
  succeeded: "Successful!",
  pending: "Payment in progress",
  cancelled: "Payment cancelled",
  failed: "Payment failed",
  declined: "Payment declined",
  rejected: "Payment rejected",
  expired: "Payment expired",
  unknown: "Payment not confirmed yet",
};

const readPaymentStatus = (responseAPI) => {
  const body = responseAPI?.data || responseAPI?.response?.data;
  return String(body?.payment_status || body?.data?.payment_status || "").toLowerCase();
};

// "succeeded" | "pending" | the reported failure ("failed", "declined", ...) |
// "unknown" when the lookup itself failed or came back without a status.
const classifyPayment = (responseAPI) => {
  const value = readPaymentStatus(responseAPI);
  if (value) {
    if (SUCCEEDED_PAYMENT_STATUSES.includes(value)) return "succeeded";
    if (PENDING_PAYMENT_STATUSES.includes(value)) return "pending";
    return value;
  }
  // Body carried no payment_status - fall back to the HTTP code, which is what this
  // screen used to key off on its own.
  return responseAPI?.status === 200 ? "succeeded" : "unknown";
};

// The message for anything that is not a settled payment.
const describePayment = (outcome) => {
  if (outcome === "pending") return PENDING_MESSAGE;
  if (!outcome || outcome === "unknown") return UNKNOWN_MESSAGE;
  return FAILURE_MESSAGES[outcome] || `Your payment could not be completed (${outcome}). Please try again or contact support if the amount was charged.`;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const trackPurchase = (amount) => {
  // fbq comes from the Meta pixel script - a missing pixel must never turn a
  // successful payment into a failure.
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq("track", "Purchase", {
      currency: "USD",
      value: amount,
    });
  } catch (e) {
    console.warn("Purchase tracking failed:", e);
  }
};

function PostPayment({status}) {
  const reduxChallengeId = useSelector((state) => state.account.selectedChallenge);
  const paymentHistory = useSelector((state) => state.account.paymentHistory);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // What the API reported in payment_status - drives which message the user sees.
  const [statusDetail, setStatusDetail] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const {idToken} = useSelector((state) => state.auth);

  const lastPayment = paymentHistory?.lastPayment || JSON.parse(localStorage.getItem("lastPayment") || "null");
  const challengeId = reduxChallengeId || localStorage.getItem("checkout_challenge_id") || lastPayment?.data?.challenge_id || lastPayment?.data?.challenge;

  const isAbandoned = ABANDONED_STATUSES.includes(status);

  // Where an abandoned/failed payment sends the user back to - the dashboard
  // checkout for a signed-in user, the public checkout page for a guest.
  const checkoutPath = challengeId ? (idToken ? `/funding-evaluation/payments/${challengeId}` : `/funding-evaluation/public/checkout/${challengeId}`) : "/";

  const {width, height} = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  const isUnmounted = useRef(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  // A cancelled redirect means the provider is telling us the user backed out
  // before paying, so there is no result to look up and nothing to tell them:
  // they go straight back to checkout with the details they filled in still on
  // the form.
  //
  // This deliberately does NOT consult the payment status API the way a declined
  // or failed redirect does. That lookup is an authenticated request, and a 401
  // or 403 on it trips the global axios interceptor, which logs the user out,
  // purges persisted state and reloads the page - which lands them back on this
  // route with no token, where the component renders nothing at all. Staking the
  // way out of a cancellation on a network call that can do that is what left the
  // user on a blank screen. Nothing was charged, so there is nothing here worth
  // that risk: a charge that did somehow settle is still picked up by the webhook
  // and shows in payment history.
  const isCancelled = status === "cancelled";

  useEffect(() => {
    if (!isCancelled) return;

    try {
      // The checkout page repopulates from this and clears it on mount.
      localStorage.setItem("checkout_restore", "true");
      localStorage.removeItem("payment_in_progress");
      localStorage.removeItem("lastPayment");
    } catch (e) {
      console.error("Error clearing payment state:", e);
    }
    dispatch(removeLastPayment());

    navigate(checkoutPath, {replace: true});
  }, [isCancelled, checkoutPath, navigate, dispatch]);

  const playSuccessSound = () => {
    try {
      const audio = new Audio(successSound);
      audio.volume = 0.6;
      audio.play().catch((e) => {
        console.warn("Sound autoplay blocked by browser policy:", e);
      });
    } catch (e) {
      console.warn("Sound autoplay blocked:", e);
    }
  };

  const handleModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    setModalOpen(true);
  }, [status]);

  const [response, setResponse] = useState(null);

  // Live counter for the processing screen, so it shows real progress instead of
  // a static "expected time" line.
  useEffect(() => {
    if (!isLoading) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [isLoading]);

  useEffect(() => {
    // Guarded across every branch below: removeLastPayment always writes a fresh
    // paymentHistory object, so dispatching it unguarded re-triggers this effect
    // (paymentHistory is a dependency) and loops until React bails with
    // "Maximum update depth exceeded".
    if (hasStartedRef.current) return;
    // Cancellations are handled by the effect above - no lookup, no popup.
    if (isCancelled) return;

    const paymentId = lastPayment?.data?.id;
    const isRapyd = lastPayment?.data?.payment_mode === "rapyd";
    // The private status endpoint needs the token, the public one needs the email.
    const canQueryStatus = Boolean(paymentId) && Boolean(idToken || lastPayment?.data?.email);

    const clearStoredPayment = () => {
      if (paymentHistory?.lastPayment) {
        dispatch(removeLastPayment());
      }
      localStorage.removeItem("lastPayment");
    };

    const clearCheckoutDetails = () => {
      localStorage.removeItem("checkout_details");
      localStorage.removeItem("checkout_challenge_id");
      localStorage.removeItem("checkout_coupon");
      localStorage.removeItem("checkout_restore");
      localStorage.removeItem("payment_in_progress");
    };

    // payment_in_progress is what PrivateRoutes uses to pull a user who wandered
    // off mid-payment back to checkout. Once the provider has redirected them
    // here the payment is no longer in flight, so the flag has to be released -
    // left set, every attempt to reach the dashboard bounces back to checkout.
    // The checkout form still has to repopulate after a payment that did not go
    // through, so that intent moves to its own key, which the checkout page
    // consumes on mount.
    const releasePaymentInProgress = () => {
      try {
        localStorage.setItem("checkout_restore", "true");
        localStorage.removeItem("payment_in_progress");
      } catch (e) {
        console.error("Error releasing payment_in_progress:", e);
      }
    };

    // A payment the API reports as cancelled was never charged, so there is
    // nothing to tell the user - back to checkout, replacing the history entry so
    // the back button does not return to this result route. (A cancelled redirect
    // never gets this far; it is handled without a lookup above.)
    const returnToCheckout = () => {
      clearStoredPayment();
      navigate(checkoutPath, {replace: true});
    };

    // 🎉 CONFETTI + SOUND
    const celebrate = (amount) => {
      setShowConfetti(true);
      playSuccessSound();
      trackPurchase(amount);
    };

    // A lookup that told us nothing falls back to what the redirect itself claimed,
    // so the popup can still name the outcome. "failure" stays unknown on purpose -
    // the provider calling it failed is not proof the charge did not settle.
    const resolveOutcome = (apiOutcome) => {
      if (apiOutcome !== "unknown") return apiOutcome;
      if (status === "cancelled") return "cancelled";
      if (status === "declined") return "declined";
      return "unknown";
    };

    // A single status check - used by the cancelled/declined/failed routes, where
    // there is no webhook still on its way to sit and wait for.
    const checkPaymentStatusOnce = async () => {
      if (idToken) {
        return getPaymentStatus(idToken, {payment_id: paymentId});
      }
      return getPaymentStatusPublic({payment_id: paymentId, email: lastPayment?.data?.email});
    };

    // Re-checks the payment status until the API reports something final, or until
    // we give up. Only a still-moving payment ("open"/"processing") is worth waiting
    // on - a reported failure is final, so we stop rather than sit on the loader.
    const pollForPaymentStatus = async (payload) => {
      const startedAt = Date.now();
      let responseAPI = null;
      let outcome = "unknown";

      while (!isUnmounted.current) {
        responseAPI = await getPaymentStatus(idToken, payload);
        outcome = classifyPayment(responseAPI);

        if (outcome !== "pending" && outcome !== "unknown") break;
        if (Date.now() - startedAt >= MAX_POLL_MS - POLL_INTERVAL_MS) break;

        await wait(POLL_INTERVAL_MS);
      }

      return {outcome, responseAPI};
    };

    // The user came back from a cancelled, declined or failed redirect. Ask the API
    // what actually happened before deciding what to show: a charge that settled is
    // treated as a success, and every other answer gets a popup naming it.
    const handleAbandonedPayment = async () => {
      setIsLoading(true);

      let responseAPI = null;
      try {
        responseAPI = await checkPaymentStatusOnce();
      } catch (error) {
        console.error("Error fetching payment status:", error);
      }
      if (isUnmounted.current) return;

      const outcome = resolveOutcome(classifyPayment(responseAPI));
      setResponse(responseAPI?.data || responseAPI?.response?.data);
      setStatusDetail(outcome);
      setIsLoading(false);

      if (outcome === "succeeded") {
        setPaymentStatus("success");
        clearCheckoutDetails();
        celebrate(lastPayment?.data?.amount);

        await wait(2500);
        clearStoredPayment();

        if (idToken) {
          navigate("/funding-evaluation-payment/payment-history/");
        } else {
          navigate("/signin", {state: {message: "Your payment went through! Please sign in to see your account."}});
        }
        return;
      }

      // Nothing was charged and the user already knows why - straight back to
      // checkout, with the details they filled in still on the form.
      if (outcome === "cancelled") {
        returnToCheckout();
        return;
      }

      clearStoredPayment();

      if (outcome === "pending") {
        // Still moving despite the redirect - say so instead of sending the user
        // back to checkout, where they could pay a second time.
        setPaymentStatus("processing");
        return;
      }

      // Cancelled, failed, declined, rejected, expired - the popup names which one,
      // and closing it is what returns the user to checkout.
      setPaymentStatus("failure");
    };

    const getPaymentStatusForChallenge = async () => {
      // Nothing on record to ask the API about, so the redirect is all we know the
      // outcome by - the popup says that much, and closing it returns to checkout.
      if (!canQueryStatus && !isRapyd) {
        if (isAbandoned) {
          hasStartedRef.current = true;
          releasePaymentInProgress();
          clearStoredPayment();
          setStatusDetail(resolveOutcome("unknown"));
          setPaymentStatus("failure");
        }
        return;
      }

      if (!idToken && !lastPayment?.data?.email) {
        window.location.href = "/signin";
        return;
      }

      hasStartedRef.current = true;
      releasePaymentInProgress();

      if (isRapyd) {
        if (status === "success") {
          setPaymentStatus("success");
          clearCheckoutDetails();
          celebrate(lastPayment?.data?.amount);

          await wait(2000);
          clearStoredPayment();
          navigate("/funding-evaluation-payment/payment-history/");
          return;
        }
        // A rapyd payment we can still identify gets the same status check as any
        // other. Without an id there is nothing to ask about, so the redirect is
        // all we know the outcome by - and a user who cancelled out of the rapyd
        // page must not be told a payment is in progress.
        if (isAbandoned) {
          if (canQueryStatus) {
            await handleAbandonedPayment();
            return;
          }
          clearStoredPayment();
          setStatusDetail(resolveOutcome("unknown"));
          setPaymentStatus("failure");
          return;
        }
        setStatusDetail("pending");
        setPaymentStatus("processing");
        return;
      }

      if (isAbandoned) {
        await handleAbandonedPayment();
        return;
      }

      try {
        setIsLoading(true);
        const payloadPublic = {payment_id: paymentId, email: lastPayment?.data?.email};
        const payload = {payment_id: paymentId};

        if (idToken) {
          // Handle token scenario
          const {outcome, responseAPI} = await pollForPaymentStatus(payload);
          if (isUnmounted.current) return;

          setResponse(responseAPI?.data || responseAPI?.response?.data);
          setStatusDetail(outcome);

          if (outcome === "succeeded") {
            setPaymentStatus("success");
            clearCheckoutDetails();

            setIsLoading(false);
            celebrate(lastPayment?.data?.amount);

            await wait(2500);

            navigate("/funding-evaluation-payment/payment-history/");
          } else if (outcome === "pending" || outcome === "unknown") {
            // Nothing final after MAX_POLL_MS - drop the spinner and tell the user it
            // is in progress rather than holding them on a loader.
            setIsLoading(false);
            setPaymentStatus("processing");
          } else {
            // The API reported a failure ("failed", "declined", ...) - show why.
            setIsLoading(false);
            setPaymentStatus("failure");
          }
        } else {
          // Handle no token scenario - a guest cannot be shown the dashboard, so the
          // outcome travels with them to sign-in.
          const responseAPI = await getPaymentStatusPublic(payloadPublic);
          if (isUnmounted.current) return;

          const outcome = classifyPayment(responseAPI);
          setResponse(responseAPI?.data || responseAPI?.response?.data);
          setStatusDetail(outcome);
          setIsLoading(false);

          if (outcome === "succeeded") {
            setPaymentStatus("success");
            clearCheckoutDetails();
            celebrate(lastPayment?.data?.amount);

            await wait(2500);
            navigate("/signin", {state: {message: "Your payment went through! Please sign in to see your account."}});
            return;
          }

          if (outcome === "pending" || outcome === "unknown") {
            setPaymentStatus("processing");
            // Redirect to sign-in with message
            navigate("/signin", {state: {message: "Your payment is being processed! Please visit your dashboard payment history to check transaction status."}});
            return;
          }

          // A reported failure - show why, and closing the modal returns them to
          // checkout so they can retry.
          setPaymentStatus("failure");
        }
      } catch (error) {
        console.error("Error fetching payment status:", error);
        setIsLoading(false);
        setPaymentStatus("failure");
        navigate(checkoutPath);
      } finally {
        dispatch(removeLastPayment());
        localStorage.removeItem("lastPayment");
        setIsLoading(false);
      }
    };

    getPaymentStatusForChallenge();
  }, [idToken, navigate, dispatch, paymentHistory, challengeId, checkoutPath, status, isAbandoned, isCancelled]);

  useEffect(() => {
    if (paymentStatus === "success") {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const accountList = useSelector((state) => state?.account?.accountList?.data);
  let msg = accountList?.length > 0 ? "Congratulations! Payment was successful" : "You'll shortly receive an email with your account login credentials";

  const resultOutcome = (() => {
    if (paymentStatus === "processing" || statusDetail === "pending") return "pending";
    if (statusDetail && statusDetail !== "unknown") return statusDetail;
    // No usable payment_status: fall back to what the old copy keyed off.
    if (response?.detail === "Unable to fetch the payment status" || lastPayment?.data?.payment_mode === "rapyd") return "pending";
    return "unknown";
  })();

  // The button and where it leads follow whatever the popup says the outcome was,
  // so a heading reading "Payment in progress" can never sit above a "Try again"
  // that drops the user back on checkout. A payment the API still calls
  // open/processing is not an error, and neither is one the user cancelled
  // themselves - both read as "Information", not "Error".
  const isPendingResult = resultOutcome === "pending";
  const isInfoResult = isPendingResult || resultOutcome === "cancelled" || resultOutcome === "unknown";
  const resultMessage = describePayment(resultOutcome);
  const resultTitle = STATUS_TITLES[resultOutcome];

  // This route renders nothing but the popup, so closing it always has to lead
  // somewhere - otherwise the user is left looking at an empty page. A payment
  // that could still settle goes to payment history rather than back to checkout,
  // where they could end up paying twice; everything else returns to checkout.
  const closeDestination = () => {
    if (!isPendingResult) {
      navigate(checkoutPath);
      return;
    }
    if (idToken) {
      navigate("/funding-evaluation-payment/payment-history/");
    } else {
      navigate("/signin", {state: {message: "Your payment is being processed! Please sign in to check its status."}});
    }
  };

  if (!idToken && !lastPayment?.data?.email) {
    return null;
  }

  // Nothing to render while a cancelled/declined payment is still being verified
  // against the status API, or while a cancellation bounces back to checkout.
  if (isAbandoned && !isLoading && !paymentStatus) {
    return null;
  }

  return (
    <>
      {paymentStatus === "success" && showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={450}
          recycle={false}
          gravity={0.5}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            // Above the success modal overlay (z-index 10000) so the confetti sits in
            // the foreground instead of behind its blurred backdrop.
            zIndex: 10001,
            pointerEvents: "none",
          }}
        />
      )}

      {isLoading && (
        <ProcessingModal
          open={isLoading}
          elapsedSeconds={elapsedSeconds}
        />
      )}
      {!isLoading &&
        isModalOpen &&
        (paymentStatus === "success" ? (
          <SuccessModal
            setModalOpen={handleModal}
            title={STATUS_TITLES.succeeded}
            desc={msg}
            btnText="Close"
          />
        ) : (
          <ErrorModal
            handleNavigation={closeDestination}
            setModalOpen={handleModal}
            info={isInfoResult}
            title={resultTitle}
            desc={resultMessage}
            btnText={isPendingResult ? "Close" : "Try again"}
          />
        ))}
    </>
  );
}

export default PostPayment;
